import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createApiKey } from '../../apis/apiKeys';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

// Simple IPv4 validation
const validateIp = (ip) => /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ip);

const CreateAPIDialog = ({ openApi, onClose, onSuccess }) => {
    const [newHost, setNewHost] = useState('');
    const [newName, setNewName] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [apiError, setApiError] = useState(null);
    const [loading, setLoading] = useState(false);
    const userGroup = useSelector((state) => state.auth.user.groups);

    // Reset form when dialog closes
    useEffect(() => {
        if (!openApi) {
            setNewHost('');
            setNewName('');
            setFormErrors({});
            setApiError(null);
            setLoading(false);
        }
    }, [openApi]);

    const handleSubmit = async () => {
        const errors = {};
        setApiError(null);

        if (!newName.trim()) errors.name = 'Name is required.';
        if (userGroup?.includes(2)) {
            if (isEmpty(newHost.trim())) errors.host = 'Host is required.';
            else if (!validateIp(newHost.trim())) errors.host = 'Invalid IP address.';
        }

        setFormErrors(errors);
        if (Object.keys(errors).length) return;

        setLoading(true);
        try {
            await createApiKey({ host: newHost.trim(), name: newName.trim() });
            toast.success('API key created successfully');
            onClose();
            onSuccess?.();
        } catch (err) {
            let apiErrMsg = 'Failed to create API key';

            if (err?.host && Array.isArray(err.host)) {
                apiErrMsg = err.host.join(' ');
                setFormErrors((prev) => ({ ...prev, host: apiErrMsg }));
            } else if (err?.name && Array.isArray(err.name)) {
                apiErrMsg = err.name.join(' ');
                setFormErrors((prev) => ({ ...prev, name: apiErrMsg }));
            } else if (typeof err === 'string') {
                apiErrMsg = err;
            } else if (err) {
                apiErrMsg = JSON.stringify(err);
            }

            setApiError(apiErrMsg);
            toast.error(apiErrMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={openApi}
            onClose={!loading ? onClose : undefined}
            maxWidth="xs"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 3,
                    padding: '12px 24px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '18px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 0',
                    borderBottom: '1px solid #E1E8ED',
                    fontFamily: 'Inter',
                }}
            >
                Create API
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        border: '2px solid #444',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        borderRadius: '6px',
                        opacity: loading ? 0.5 : 1,
                    }}
                    onClick={!loading ? onClose : undefined}
                >
                    <CloseIcon sx={{ fontSize: '14px', color: '#444' }} />
                </div>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ px: 0, py: 3 }}>
                <div className="profile_form">
                    <div className="form_group" style={{ width: '100%' }}>
                        <label className="input_label">Name</label>
                        <TextField
                            size="small"
                            placeholder="Enter key name (e.g. My API Key)"
                            value={newName}
                            onChange={(e) => {
                                setNewName(e.target.value);
                                setFormErrors((prev) => ({ ...prev, name: undefined }));
                            }}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                            disabled={loading}
                            sx={{
                                fontFamily: 'Inter',
                                '& .MuiInputBase-input, & .MuiInputLabel-root': {
                                    fontSize: 14,
                                },
                            }}
                        />
                    </div>

                    <div className="form_group" style={{ width: '100%' }}>
                        <label className="input_label">Host</label>
                        <TextField
                            size="small"
                            placeholder="Enter host (e.g. 192.168.1.1)"
                            value={newHost}
                            onChange={(e) => {
                                setNewHost(e.target.value);
                                setFormErrors((prev) => ({ ...prev, host: undefined }));
                            }}
                            error={!!formErrors.host}
                            helperText={formErrors.host}
                            disabled={loading}
                            sx={{
                                fontFamily: 'Inter',
                                '& .MuiInputBase-input, & .MuiInputLabel-root': {
                                    fontSize: 14,
                                },
                            }}
                        />
                    </div>

                    {apiError && (
                        <div
                            className="alert alert-danger mt-3 rounded-3 border-0 shadow-sm py-2 px-3"
                            style={{ fontSize: 15 }}
                        >
                            {apiError}
                        </div>
                    )}
                </div>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 0,
                    pt: 3,
                    borderTop: '1px solid #E1E8ED',
                    gap: 2,
                }}
            >
                <button className="custom_btn secondary" onClick={onClose} disabled={loading}>
                    Cancel
                </button>
                <button
                    className="custom_btn"
                    onClick={handleSubmit}
                    disabled={(userGroup?.includes(2) && isEmpty(newHost.trim())) || !newName.trim() || loading}
                >
                    {loading ? 'Creating...' : 'Submit'}
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateAPIDialog;
